"use client"

import { useState } from "react"
import { Form, Formik } from "formik"
import { Building2, Loader2, LogOut } from "lucide-react"
import { useTranslations } from "next-intl"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { FormInput } from "@/components/form-inputs/form-input"
import { UiErrorText } from "@/components/i18n/ui-error-text"
import { useAuth } from "@/hooks/use-auth"
import { useImpersonation } from "@/hooks/use-impersonation"
import type { TenantListEntry } from "@/hooks/queries/tenants"
import { organizationReturnTo } from "@/lib/auth/organization-selection"

interface Props {
  tenants: TenantListEntry[]
  homeTenantId: string
  loadFailed: boolean
  retrying: boolean
  onRetry: () => void
}

export function StaffOrganizationSelector({ tenants, homeTenantId, loadFailed, retrying, onRetry }: Props) {
  const t = useTranslations("Shell.StaffOrganizationSelector")
  const common = useTranslations("Common")
  const { switching, impersonate, stopImpersonation } = useImpersonation({
    returnTo: () => organizationReturnTo(new URLSearchParams(window.location.search).get("returnTo")),
  })
  const { logout } = useAuth()
  const [signingOut, setSigningOut] = useState(false)
  const [logoutError, setLogoutError] = useState<string | null>(null)

  async function handleLogout() {
    setSigningOut(true)
    setLogoutError(null)
    try {
      await logout()
    } catch (error) {
      setLogoutError(error instanceof Error ? error.message : t("signOutFailed"))
    } finally {
      setSigningOut(false)
    }
  }

  return (
    <main data-document-scroll="always" className="box-border w-full min-w-0 max-w-[100%] min-h-dvh bg-background px-3 py-4 pb-[max(1rem,env(safe-area-inset-bottom))] sm:px-6 sm:py-12" style={{ width: "100%", maxWidth: "100vw", minWidth: 0 }}>
      <Card className="mx-auto w-full min-w-0 max-w-xl overflow-hidden">
        <CardHeader className="space-y-3 p-4 sm:p-6">
          <Building2 className="mb-2 h-8 w-8 text-primary" aria-hidden="true" />
          <CardTitle className="break-words text-xl leading-snug sm:text-2xl"><h1>{t("title")}</h1></CardTitle>
          <CardDescription className="leading-relaxed"><span>{t("description")}</span></CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 px-4 pb-4 pt-0 sm:px-6 sm:pb-6">
          {loadFailed ? (
            <div className="space-y-3">
              <p role="alert" className="text-sm text-destructive"><span>{t("loadFailed")}</span></p>
              <Button type="button" variant="outline" onClick={onRetry} disabled={retrying || signingOut}>
                <span>{common("retry")}</span>
              </Button>
            </div>
          ) : (
            <Formik initialValues={{ search: "", tenantId: "" }} onSubmit={async values => {
              if (!tenants.some(tenant => tenant.id === values.tenantId)) return
              if (values.tenantId === homeTenantId) await stopImpersonation()
              else await impersonate(values.tenantId)
            }}>
              {({ values, isSubmitting, setFieldValue, submitForm }) => {
                const matches = tenants.filter(tenant => tenant.name.toLocaleLowerCase().includes(values.search.trim().toLocaleLowerCase()))
                const busy = switching || isSubmitting || signingOut
                return (
                  <Form className="space-y-4">
                    <FormInput name="search" label={t("search")} autoComplete="off" disabled={busy} className="h-12" />
                    <div className="space-y-2 p-1 sm:max-h-[50dvh] sm:overflow-y-auto" aria-busy={busy}>
                      {matches.map(tenant => (
                        <Button key={tenant.id} type="button" variant="outline" disabled={busy}
                          className="h-auto min-h-14 w-full justify-start gap-3 whitespace-normal px-3 py-3 text-left"
                          onClick={async () => { await setFieldValue("tenantId", tenant.id, false); await submitForm() }}>
                          {busy && values.tenantId === tenant.id
                            ? <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden="true" />
                            : <Building2 className="h-4 w-4 shrink-0" aria-hidden="true" />}
                          <span className="min-w-0 flex-1 [overflow-wrap:anywhere] leading-relaxed">{tenant.name}</span>
                        </Button>
                      ))}
                      {matches.length === 0 && <p role="status" className="p-3 text-sm text-muted-foreground"><span>{t("noMatches")}</span></p>}
                    </div>
                  </Form>
                )
              }}
            </Formik>
          )}
          {logoutError && <p role="alert" className="text-sm text-destructive"><UiErrorText message={logoutError} /></p>}
          <Button type="button" variant="ghost" className="min-h-12 w-full whitespace-normal" onClick={() => void handleLogout()} disabled={switching || signingOut}>
            <LogOut className="mr-2 h-4 w-4" aria-hidden="true" /><span>{t("signOut")}</span>
          </Button>
        </CardContent>
      </Card>
    </main>
  )
}
